import {
  type CreatorsV2Filter,
  makeQueryParams,
  normalizeUsers,
  type UserItem,
} from '@metapic-apps/shared'
import { type Ref, ref, toValue, watch, watchEffect } from 'vue'

import { handleErrorMessage } from '@/lib/helpers/error.helpers'
import { request } from '@/lib/services'
import { useErrorStore } from '@/stores/error.stores'

type UseGetUserListOptions = {
  /**
   * Narrows the list to creators who already have affiliate links for the advertiser in scope.
   */
  hasAffiliateLinks?: boolean
}

export const useGetUserList = (
  filter: Ref<CreatorsV2Filter>,
  { hasAffiliateLinks = false }: UseGetUserListOptions = {},
) => {
  const data = ref<UserItem[]>([])
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchUsers = async (params: URLSearchParams | undefined) => {
    try {
      data.value = []
      isLoading.value = true
      const response = await request.get('/v2/users', { params })
      data.value = normalizeUsers(response.data.data)
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  watchEffect(() => {
    const query = filter.value.query?.trim()?.toLowerCase()

    const params = makeQueryParams({
      advertiserGroupId: filter.value.advertiserGroup?.id,
      advertiserId: filter.value.advertiser?.id,
      query,
      status: filter.value.status,
      sortBy: query ? 'username:asc' : 'last_active:desc',
      size: '10',
    })
    if (hasAffiliateLinks) {
      params?.set('has_affiliate_links', 'true')
    }
    if (params) {
      void fetchUsers(params)
    }
  })

  return {
    data,
    isLoading,
    error,
  }
}

export const loadCreatorsForContentLibrary = (filterRef: Ref<CreatorsV2Filter>) =>
  useGetUserList(filterRef, { hasAffiliateLinks: true })

/**
 * Resolves a pasted list of creator ids and e-mail addresses in one go, for the segment form's
 * bulk add. `/v2/match-users` is scoped by advertiser group only, so the caller passes the group
 * the advertiser belongs to.
 */
export const useGetUsersFromArray = (
  advertiserGroupId: Ref<number | undefined>,
  users: Ref<string[]>,
) => {
  const data = ref<{ matched: UserItem[]; unmatched: string[] }>({ matched: [], unmatched: [] })
  const isLoading = ref(false)
  const error = ref<Error | null>(null)
  const errorStore = useErrorStore()

  const fetchUsers = async (tokens: string[], groupId: number) => {
    try {
      data.value = { matched: [], unmatched: [] }
      isLoading.value = true
      error.value = null

      const response = await request.post('/v2/match-users', {
        advertiser_group_id: groupId,
        user_emails_or_ids: tokens,
      })
      data.value = {
        matched: normalizeUsers(response.data.matched_users),
        unmatched: response.data.unmatched_users,
      }
    } catch (e: unknown) {
      errorStore.setError(handleErrorMessage(e))
      error.value = e as Error
    } finally {
      isLoading.value = false
    }
  }

  /*
   * The group is watched alongside the tokens: an omitted `advertiser_group_id` is serialised away
   * and the endpoint reads that as every creator on the platform rather than none, so tokens pasted
   * before the advertiser has loaded wait here and go out once the scope arrives.
   */
  watch(
    [users, advertiserGroupId],
    () => {
      const tokens = toValue(users)
      const groupId = advertiserGroupId.value

      if (tokens.length && groupId !== undefined) {
        return fetchUsers(tokens, groupId)
      }
    },
    { deep: true },
  )

  return { data, isLoading, error }
}
